/**
 * A labelled group of fields inside one form (docs/04 §3, docs/03 §5).
 *
 * PropOS v1 split the property form into TABS — Info, Address, Access,
 * Security, Hours. v2 uses sections in one scrolling form instead, deliberately:
 * docs/03 names "fake tab-modules" as a Beta failure, and inside a FORM the
 * problem is concrete rather than aesthetic. Tabs hide fields, so a validation
 * error can land on a pane nobody is looking at, an unsaved change can be
 * invisible while the user clicks away, and "did I fill that in?" needs four
 * clicks to answer. One form, one save, everything reachable by scrolling —
 * and on a phone a section heading is a better landmark than a tab strip that
 * has to scroll horizontally.
 *
 * Rendered as a real `fieldset`/`legend` so screen readers announce which group
 * a field belongs to, which is the entire point of grouping.
 */

import type { ReactNode } from 'react';

export function FormSection({
  title,
  description,
  children,
}: {
  readonly title: string;
  /** One line on what belongs here, when the title alone is ambiguous. */
  readonly description?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <fieldset className="flex min-w-0 flex-col gap-4 border-t border-hairline pt-4">
      <legend className="float-left mb-1 w-full text-meta font-semibold uppercase tracking-wide text-ink2">
        {title}
        {description === undefined ? null : (
          <span className="mt-0.5 block text-chip font-normal normal-case tracking-normal text-ink2">
            {description}
          </span>
        )}
      </legend>
      {children}
    </fieldset>
  );
}
